import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageRootLanding } from '../../../ui/components/PageBuilder/PageRootLanding/PageRootLanding';
import { Button } from '../../../ui/components/Button/Button';
import { Heading } from '../../../ui/components/Heading/Heading';
import { Icon } from '../../../ui/components/Icon';
import { ParagraphProps } from '../../../components/PageBuilder/Paragraph/Paragraph';
import { AppointmentModal } from '../../components/AppointmentModal/AppointmentModal';
import { JESSICA_SERVICES } from '../../components/AppointmentModal/Services';
import { OtherServices } from '../OtherServices/OtherServices';
import { JessicaArticlesCards } from '../OtherServices/JessicaArticlesCards';
import styles from './NotePage.module.scss';

interface NotePageProps {
  title: string;
  eyebrow: string;
  summary: string;
  paragraphs: ParagraphProps[];
  relatedTitle: string;
  appointmentServiceValue?: string;
  readTime?: string;
}

export const NotePage = ({
  title,
  eyebrow,
  summary,
  paragraphs,
  relatedTitle,
  appointmentServiceValue = relatedTitle,
  readTime = '4 min read',
}: NotePageProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const heroImage = paragraphs.find((paragraph) => paragraph.img)?.img;
  const appointmentService = JESSICA_SERVICES.find(
    (service) => service.value === appointmentServiceValue
  );
  const relatedNotes = JessicaArticlesCards.filter(
    (card) => card.title !== relatedTitle
  );

  const renderSections = () =>
    paragraphs.map((paragraph) => (
      <section
        className={styles.section}
        key={paragraph.title || paragraph.text}
      >
        {paragraph.title && (
          <Heading className={styles['section-title']} headingLevel="h2">
            {paragraph.title}
          </Heading>
        )}
        <p className={styles.paragraph}>{paragraph.text}</p>
      </section>
    ));

  return (
    <PageRootLanding
      colorPalettes="doctor-1"
      title={title}
      imageLinkPreview={heroImage}
      configuration={{ showTitle: false }}
      navbarItems={[
        {
          endpoint: '/',
          icon: 'home1',
          name: 'Go home',
          onClick: () => {
            navigate('/');
          },
        },
      ]}
    >
      <>
        <AppointmentModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          defaultService={appointmentService}
        />
        <main className={styles.root}>
          <article className={styles.article}>
            <header className={styles.hero}>
              <div className={styles['hero-copy']}>
                <span className={styles.eyebrow}>{eyebrow}</span>
                <Heading className={styles.title} headingLevel="h1">
                  {title}
                </Heading>
                <p className={styles.summary}>{summary}</p>
                <ul className={styles.meta}>
                  <li>Educational note</li>
                  <li>{readTime}</li>
                  <li>Private consultation available</li>
                </ul>
                <div className={styles.actions}>
                  <Button
                    className={styles.button}
                    btnStyle="secondary"
                    icon="calendar"
                    iconPlace="left"
                    glassEffect
                    text="Book an appointment"
                    onClick={() => setIsOpen(true)}
                  />
                  <Button
                    className={styles.button}
                    btnStyle="primary"
                    icon="left-arrow"
                    iconPlace="left"
                    glassEffect
                    text="Back to notes"
                    to="/wellness-notes"
                  />
                </div>
              </div>
              {heroImage && (
                <figure className={styles.figure}>
                  <img src={heroImage} alt={title} />
                  <figcaption>
                    A readable overview to help prepare better questions for a
                    medical visit.
                  </figcaption>
                </figure>
              )}
            </header>
            <div className={styles.layout}>
              <div className={styles.content}>
                <aside className={styles.disclaimer}>
                  <Icon type="info" alt="" classname={styles['info-icon']} />
                  <p>
                    This article is educational and does not replace a medical
                    evaluation. Individual recommendations should be reviewed in
                    consultation.
                  </p>
                </aside>
                {renderSections()}
                <section className={styles.cta}>
                  <div>
                    <span className={styles.eyebrow}>Next step</span>
                    <Heading className={styles['cta-title']} headingLevel="h2">
                      Bring the topic into a private appointment
                    </Heading>
                    <p>
                      Use this note as a starting point. A clinical conversation
                      can connect your symptoms, goals, history, and questions.
                    </p>
                  </div>
                  <Button
                    className={styles.button}
                    btnStyle="secondary"
                    icon="calendar"
                    iconPlace="left"
                    glassEffect
                    text="Book an appointment"
                    onClick={() => setIsOpen(true)}
                  />
                </section>
              </div>
              <aside className={styles.sidebar}>
                <OtherServices
                  title="Related notes"
                  articleCards={relatedNotes}
                />
              </aside>
            </div>
          </article>
        </main>
      </>
    </PageRootLanding>
  );
};
